import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppHeader } from '@/components/AppHeader';
import { PlayerNameLink } from '@/components/PlayerNameLink';
import { getPublicStartingList } from '@/lib/api';
import { viewerHighlight } from '@/lib/viewerRow';
import { useAuth } from '@/contexts/AuthContext';
import type { StartingListResponse } from '@swiss-magic/shared-types';
import { getGameConfig } from '@swiss-magic/shared-types';
import { ratingFieldLabel, externalIdFieldLabel } from '@/lib/game-labels';

export function StartingListPage() {
  const { t } = useTranslation('tournament');
  const { t: tc } = useTranslation('common');
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const [data, setData] = useState<StartingListResponse | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    getPublicStartingList(slug)
      .then((result) => setData(result))
      .catch((err: unknown) => {
        setMessage(err instanceof Error ? err.message : t('starting_list.load_failed'));
      })
      .finally(() => setLoading(false));
    // t intentionally omitted — stable across renders, including it
    // would re-fetch on every language change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p>{t('loading')}</p>
      </div>
    );
  }

  return (
    <div data-testid="starting-list-page" className="min-h-screen bg-background">
      <AppHeader />

      <main className="mx-auto max-w-4xl px-4 md:px-6 py-6">
        {data && (
          <div className="mb-6">
            <h2 className="text-xl md:text-2xl font-semibold mb-1">{data.tournamentName}</h2>
            <p className="text-sm text-muted-foreground mb-3">{t('starting_list.subtitle')}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <Link
                to={`/t/${slug}`}
                className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
              >
                {t('common.section_labels.info')}
              </Link>
              <Link
                to={`/t/${slug}/standings`}
                className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
              >
                {t('common.section_labels.standings')}
              </Link>
            </div>
          </div>
        )}

        {message && (
          <p data-testid="starting-list-message" className="text-center text-muted-foreground">
            {message}
          </p>
        )}

        {data && (() => {
          const config = getGameConfig(data.game);
          return (
          <div className="overflow-x-auto rounded border">
            <table data-testid="starting-list-table" className="w-full text-left">
              <thead className="border-b bg-muted/50">
                <tr>
                  <th className="w-12 px-2 py-2 text-xs font-medium text-muted-foreground">{t('starting_list.table.rank')}</th>
                  <th className="px-2 py-2 text-xs font-medium text-muted-foreground">{t('starting_list.table.name')}</th>
                  <th className="w-32 px-2 py-2 text-xs font-medium text-muted-foreground">{t('starting_list.table.club_city')}</th>
                  {config.playerFields.rating && (
                    <th className="w-20 px-2 py-2 text-xs font-medium text-muted-foreground">{ratingFieldLabel(config, tc)}</th>
                  )}
                  {config.playerFields.externalId && (
                    <th className="w-24 px-2 py-2 text-xs font-medium text-muted-foreground">{externalIdFieldLabel(config, tc)}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {data.players.map((player) => {
                  const name = `${player.firstName} ${player.lastName}`.trim();
                  const mine = viewerHighlight(player.profileUserId, user?.id);
                  return (
                    <tr
                      key={player.startingRank}
                      data-testid={`starting-list-row-${player.startingRank}`}
                      data-viewer-row={mine.isMine ? 'true' : undefined}
                      className={`border-b hover:bg-muted/30 ${mine.className}`}
                    >
                      <td className="px-2 py-2 text-sm font-medium">{player.startingRank}</td>
                      <td className="px-2 py-2 text-sm">
                        <PlayerNameLink
                          slug={slug!}
                          startingRank={player.pairingRank}
                          name={name}
                        />
                      </td>
                      <td className="px-2 py-2 text-sm text-muted-foreground">
                        {player.club ?? player.city ?? ''}
                      </td>
                      {config.playerFields.rating && (
                        <td className="px-2 py-2 text-sm text-muted-foreground">
                          {player.rating ?? ''}
                        </td>
                      )}
                      {config.playerFields.externalId && (
                        <td className="px-2 py-2 text-sm text-muted-foreground">
                          {player.fideId ?? ''}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          );
        })()}
      </main>
    </div>
  );
}
